import React, { useEffect, useState } from 'react';
import {
  IAuditoriaExtratoGrupo,
  IAuditoriaExtratoLinha,
  IAuditoriaExtratoResumo,
} from '../interfaces/IAuditoriaEntradaDados';
import { AuditoriaEntradaDadosService } from '../services/AuditoriaEntradaDadosService';
import AuditMessage from './AuditMessage';

interface Props {
  esteiraId: number;
  cartaoId: number;
  contaLabel: string;
  onFechar: () => void;
}

type Aba = 'CONCILIADOS' | 'EXTRATO' | 'CONTABILIDADE';

const POR_PAGINA = 20;

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const data = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
const NATUREZA: Record<string, string> = { C: 'Crédito', D: 'Débito' };

const LinhasTabela: React.FC<{ linhas: IAuditoriaExtratoLinha[]; comArquivo?: boolean }> = ({ linhas, comArquivo }) => (
  <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-1 fs-8 mb-0'>
    <thead>
      <tr className='fw-bolder text-muted'>
        <th style={{ width: 90 }}>Data</th>
        <th>Histórico</th>
        {comArquivo && <th>Arquivo</th>}
        <th style={{ width: 80 }}>Natureza</th>
        <th className='text-end' style={{ width: 130 }}>Valor</th>
      </tr>
    </thead>
    <tbody>
      {linhas.map((linha) => (
        <tr key={linha.id}>
          <td>{data(linha.data)}</td>
          <td>{linha.historico || '—'}</td>
          {comArquivo && <td className='text-muted'>{linha.arquivo || '—'}</td>}
          <td>{NATUREZA[linha.tipo] ?? linha.tipo}</td>
          <td className='text-end'>{brl(linha.valor)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// DEVN3-662 — resultado do cruzamento do extrato bancário com a contabilidade. É por CONTA, não
// por arquivo: todos os extratos importados para a conta no período entram no mesmo cruzamento.
// Totais e percentual vêm do backend; a contabilidade do cliente não é alterada por esta auditoria.
const AuditReconciliationResult: React.FC<Props> = ({ esteiraId, cartaoId, contaLabel, onFechar }) => {
  const [resumo, setResumo] = useState<IAuditoriaExtratoResumo | undefined>();
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState<Aba>('CONCILIADOS');
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [total, setTotal] = useState(0);
  const [grupos, setGrupos] = useState<IAuditoriaExtratoGrupo[]>([]);
  const [linhas, setLinhas] = useState<IAuditoriaExtratoLinha[]>([]);

  useEffect(() => {
    let ativo = true;
    AuditoriaEntradaDadosService.buscarResumo(esteiraId, cartaoId)
      .then((r) => ativo && setResumo(r))
      .catch((err) => ativo && setErro(err?.response?.data?.message || 'Não foi possível carregar o resultado.'));
    return () => {
      ativo = false;
    };
  }, [esteiraId, cartaoId]);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);

    const busca =
      aba === 'CONCILIADOS'
        ? AuditoriaEntradaDadosService.buscarConciliados(esteiraId, cartaoId, pagina, POR_PAGINA).then((r) => {
            if (!ativo) return;
            setGrupos(r.items);
            setTotal(r.total);
          })
        : (aba === 'EXTRATO'
            ? AuditoriaEntradaDadosService.buscarExtratoPendente(esteiraId, cartaoId, pagina, POR_PAGINA)
            : AuditoriaEntradaDadosService.buscarContabilidadePendente(esteiraId, cartaoId, pagina, POR_PAGINA)
          ).then((r) => {
            if (!ativo) return;
            setLinhas(r.items);
            setTotal(r.total);
          });

    busca
      .catch((err) => ativo && setErro(err?.response?.data?.message || 'Não foi possível carregar o resultado.'))
      .finally(() => ativo && setCarregando(false));

    return () => {
      ativo = false;
    };
  }, [esteiraId, cartaoId, aba, pagina]);

  const trocarAba = (nova: Aba) => {
    setAba(nova);
    setPagina(1);
  };

  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const abas: { id: Aba; label: string; quantidade?: number }[] = [
    { id: 'CONCILIADOS', label: 'Conciliados', quantidade: resumo?.grupos },
    { id: 'EXTRATO', label: 'Extrato sem contabilidade', quantidade: resumo?.extratoPendentes },
    { id: 'CONTABILIDADE', label: 'Contabilidade sem extrato', quantidade: resumo?.contabilidadePendentes },
  ];

  return (
    <div className='audit-modal-backdrop' onClick={onFechar}>
      <div className='audit-modal audit-modal-xl' onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true'>
        <div className='audit-modal-header'>
          <div>
            <h4>Resultado — Extrato × Contabilidade</h4>
            <p className='text-muted mb-0 fs-8'>{resumo?.contaLabel || contaLabel}</p>
          </div>
          <button type='button' className='btn-close' aria-label='Fechar' onClick={onFechar} />
        </div>

        <div className='audit-modal-body'>
          {erro && (
            <div className='mb-3'>
              <AuditMessage type='ERROR' title={erro} />
            </div>
          )}

          {resumo && (
            <>
              <div className='audit-result-resumo'>
                <div className='audit-result-card'>
                  <div className='audit-result-card-titulo'>Conciliado</div>
                  <div className='audit-result-card-valor'>{resumo.percentualConciliado}%</div>
                  <div className='audit-result-card-meta'>
                    {resumo.extratoConciliados} de {resumo.extratoTotal} lançamento(s) do extrato ·{' '}
                    {resumo.arquivosConsiderados} arquivo(s)
                  </div>
                </div>
                <div className='audit-result-card'>
                  <div className='audit-result-card-titulo'>Extrato</div>
                  <div className='audit-result-card-valor'>{brl(resumo.extratoValorTotal)}</div>
                  <div className='audit-result-card-meta'>
                    Conciliado: {brl(resumo.extratoValorConciliado)} · Sem contabilidade:{' '}
                    <b>{brl(resumo.extratoValorPendente)}</b>
                  </div>
                </div>
                <div className='audit-result-card'>
                  <div className='audit-result-card-titulo'>Contabilidade</div>
                  <div className='audit-result-card-valor'>{brl(resumo.contabilidadeValorTotal)}</div>
                  <div className='audit-result-card-meta'>
                    Conciliado: {brl(resumo.contabilidadeValorConciliado)} · Sem extrato:{' '}
                    <b>{brl(resumo.contabilidadeValorPendente)}</b>
                  </div>
                </div>
              </div>

              {resumo.extratoTotal === 0 && (
                <div className='mb-3'>
                  <AuditMessage
                    type='INFO'
                    title='Nenhum extrato processado para esta conta'
                    message='Importe um extrato em PDF ou OFX para cruzar com a contabilidade.'
                  />
                </div>
              )}
            </>
          )}

          <div className='audit-result-abas'>
            {abas.map((a) => (
              <button
                key={a.id}
                type='button'
                className={`audit-result-aba ${aba === a.id ? 'audit-result-aba-ativa' : ''}`}
                onClick={() => trocarAba(a.id)}
              >
                {a.label}
                {a.quantidade !== undefined && <span className='badge badge-light ms-2'>{a.quantidade}</span>}
              </button>
            ))}
          </div>

          {carregando ? (
            <div className='text-muted text-center py-5'>Carregando...</div>
          ) : total === 0 ? (
            <div className='text-muted text-center py-5'>Nenhum lançamento nesta situação.</div>
          ) : aba === 'CONCILIADOS' ? (
            <div className='d-flex flex-column gap-3'>
              {grupos.map((grupo) => (
                <div key={grupo.chave} className='audit-result-grupo'>
                  <div className='audit-result-grupo-topo'>
                    <span>{data(grupo.data)}</span>
                    <span className='fw-bold'>{brl(grupo.valor)}</span>
                  </div>
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <div className='audit-result-lado'>Extrato bancário</div>
                      <LinhasTabela linhas={grupo.extrato} />
                    </div>
                    <div className='col-md-6'>
                      <div className='audit-result-lado'>Contabilidade</div>
                      <LinhasTabela linhas={grupo.contabilidade} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <LinhasTabela linhas={linhas} comArquivo={aba === 'EXTRATO'} />
          )}

          {totalPaginas > 1 && (
            <div className='d-flex justify-content-between align-items-center mt-3'>
              <span className='text-muted fs-8'>
                Página {pagina} de {totalPaginas} · {total} registro(s)
              </span>
              <div className='d-flex gap-2'>
                <button type='button' className='btn btn-sm btn-light' disabled={pagina <= 1 || carregando} onClick={() => setPagina(pagina - 1)}>
                  Anterior
                </button>
                <button type='button' className='btn btn-sm btn-light' disabled={pagina >= totalPaginas || carregando} onClick={() => setPagina(pagina + 1)}>
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>

        <div className='audit-modal-footer'>
          <button type='button' className='btn btn-sm btn-light' onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditReconciliationResult;
